import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ConfirmationModalComponent } from '../../shared/components/confirmation-modal.component';
import { centralMidnight, formatLastDayOfAccess, formatMembershipDate } from '../../shared/utils/membership-dates.utils';

/** Mirrors Hub's `MembershipSummary` (complimentary-membership-service.ts). */
export interface OrganizationMembership {
  tier: 'core' | 'pro';
  status: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  complimentary: boolean;
  grantedBy: string | null;
  reason: string | null;
}

const DEFAULT_EXPIRY_DATE = '2027-01-01';

/**
 * Admin "Membership" card for the org overview tab (ADN-2513): shows
 * Core/Pro, Complimentary/Paid and expiry, and offers Grant Pro (when the org
 * isn't Pro) or Revoke (only for complimentary grants — a paid subscription
 * is never offered a Revoke, and Hub refuses it regardless).
 */
@Component({
  selector: 'app-organization-membership-card',
  standalone: true,
  imports: [CommonModule, FormsModule, ConfirmationModalComponent],
  template: `
    <div class="card border-0 bg-transparent shadow-none">
      <div class="card-header bg-transparent px-0 d-flex justify-content-between align-items-center">
        <h6 class="mb-0"><i class="bi bi-award me-2"></i>Membership</h6>
        <ng-container *ngIf="membership">
          <button *ngIf="membership.tier === 'core'" type="button" class="btn btn-sm btn-primary" (click)="openGrant()">
            Grant Pro
          </button>
          <button *ngIf="membership.tier === 'pro' && membership.complimentary" type="button"
                  class="btn btn-sm btn-outline-danger" (click)="showRevoke = true">
            Revoke
          </button>
        </ng-container>
      </div>
      <div class="card-body px-0">
        <div *ngIf="!membership" class="text-body-secondary small">
          Membership status is unavailable right now. Reload to try again.
        </div>
        <ng-container *ngIf="membership">
          <div class="d-flex flex-wrap gap-2 mb-2">
            <span class="badge" [ngClass]="membership.tier === 'pro' ? 'bg-success' : 'bg-secondary'">
              {{ membership.tier === 'pro' ? 'Pro' : 'Core' }}
            </span>
            <span *ngIf="membership.tier === 'pro'" class="badge"
                  [ngClass]="membership.complimentary ? 'bg-info text-dark' : 'bg-primary'">
              {{ membership.complimentary ? 'Complimentary' : 'Paid' }}
            </span>
          </div>
          <div *ngIf="membership.tier === 'pro' && membership.currentPeriodEnd" class="small">
            {{ membership.cancelAtPeriodEnd ? 'Access through' : 'Renews' }}
            <strong>{{ membership.cancelAtPeriodEnd ? formatLastDay(membership.currentPeriodEnd) : formatDate(membership.currentPeriodEnd) }}</strong>
          </div>
          <div *ngIf="membership.complimentary && membership.reason" class="text-body-secondary small mt-1">
            Reason: {{ membership.reason }}
          </div>
          <div *ngIf="membership.tier === 'core' && membership.complimentary" class="text-body-secondary small">
            A complimentary membership has ended.
          </div>
        </ng-container>
        <div *ngIf="errorMessage" class="alert alert-danger py-2 small mt-3 mb-0" role="alert">{{ errorMessage }}</div>
      </div>
    </div>

    <ng-container *ngIf="showGrant">
      <div class="modal-backdrop fade show" (click)="closeGrant()"></div>
      <div class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true">
        <div class="modal-dialog modal-dialog-centered" role="document">
          <form class="modal-content" (ngSubmit)="grant()" #grantForm="ngForm">
            <div class="modal-header">
              <h5 class="modal-title"><i class="bi bi-award me-2"></i>Grant Pro</h5>
              <button type="button" class="btn-close" (click)="closeGrant()" [disabled]="processing" aria-label="Close"></button>
            </div>
            <div class="modal-body">
              <p class="small text-body-secondary">
                Gives {{ organizationName }} a complimentary Pro membership. No charge is made, and it
                ends at midnight Central on the date below, so the last day of access is the day before.
              </p>
              <div class="mb-3">
                <label for="grantExpiry" class="form-label">Access ends (12:00 AM Central on this date)</label>
                <input id="grantExpiry" name="expiry" type="date" class="form-control" required
                       [(ngModel)]="expiryDate" [min]="minExpiryDate" [disabled]="processing" />
              </div>
              <div class="mb-0">
                <label for="grantReason" class="form-label">Reason</label>
                <input id="grantReason" name="reason" type="text" class="form-control" required maxlength="400"
                       placeholder="e.g. 2026 existing member" [(ngModel)]="reason" [disabled]="processing" />
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" (click)="closeGrant()" [disabled]="processing">Cancel</button>
              <button type="submit" class="btn btn-primary" [disabled]="processing || grantForm.invalid || !reason.trim()">
                <span *ngIf="processing" class="spinner-border spinner-border-sm me-2" role="status"></span>
                {{ processing ? 'Granting...' : 'Grant Pro' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </ng-container>

    <app-confirmation-modal
      *ngIf="showRevoke"
      title="Revoke Complimentary Pro"
      [message]="'Revoke the complimentary Pro membership for ' + organizationName + '? It ends immediately and the organization returns to Core.'"
      confirmLabel="Revoke"
      processingLabel="Revoking..."
      variant="danger"
      icon="bi bi-award"
      [processing]="processing"
      (confirmed)="revoke()"
      (cancelled)="showRevoke = false">
    </app-confirmation-modal>
  `
})
export class OrganizationMembershipCardComponent {
  @Input() organizationId!: string;
  @Input() organizationName = '';
  @Input() membership: OrganizationMembership | null = null;
  /** Emits a human-readable summary after a grant/revoke so the page can reload and confirm. */
  @Output() changed = new EventEmitter<string>();

  showGrant = false;
  showRevoke = false;
  processing = false;
  errorMessage = '';
  expiryDate = DEFAULT_EXPIRY_DATE;
  reason = '';
  readonly minExpiryDate = new Date().toISOString().slice(0, 10);

  constructor(private http: HttpClient) {}

  openGrant(): void {
    this.errorMessage = '';
    this.expiryDate = DEFAULT_EXPIRY_DATE;
    this.reason = '';
    this.showGrant = true;
  }

  closeGrant(): void {
    if (!this.processing) this.showGrant = false;
  }

  async grant(): Promise<void> {
    if (this.processing || !this.reason.trim() || !this.expiryDate) return;
    this.processing = true;
    this.errorMessage = '';
    try {
      await firstValueFrom(
        this.http.post(`${environment.apiUrl}/admin/organizations/${this.organizationId}/membership/grant`, {
          expiresAt: centralMidnight(this.expiryDate).toISOString(),
          reason: this.reason.trim()
        })
      );
      this.showGrant = false;
      this.changed.emit(`Pro granted to "${this.organizationName}" through ${this.formatLastDay(centralMidnight(this.expiryDate).toISOString())}.`);
    } catch (error: any) {
      this.errorMessage = error?.error?.error || 'Failed to grant Pro membership';
      this.showGrant = false;
    } finally {
      this.processing = false;
    }
  }

  async revoke(): Promise<void> {
    if (this.processing) return;
    this.processing = true;
    this.errorMessage = '';
    try {
      await firstValueFrom(
        this.http.post(`${environment.apiUrl}/admin/organizations/${this.organizationId}/membership/revoke`, {})
      );
      this.showRevoke = false;
      this.changed.emit(`Complimentary Pro revoked for "${this.organizationName}".`);
    } catch (error: any) {
      this.errorMessage = error?.error?.error || 'Failed to revoke Pro membership';
      this.showRevoke = false;
    } finally {
      this.processing = false;
    }
  }

  formatDate(iso: string): string {
    return formatMembershipDate(iso);
  }

  formatLastDay(iso: string): string {
    return formatLastDayOfAccess(iso);
  }
}
